import { useCallback, useLayoutEffect, useRef, useState } from 'react'

const TAG_HEIGHT = 22
const TAG_GAP = 4
const MAX_ROWS = 2
/** 2 rows of 22px tags with 4px gap = 48px */
const MAX_HEIGHT = MAX_ROWS * TAG_HEIGHT + (MAX_ROWS - 1) * TAG_GAP

export const useVisibleTagCount = (totalCount: number) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState<number | null>(null)
  const isMeasuring = visibleCount === null

  const measure = useCallback(() => {
    const container = containerRef.current

    if (!container || totalCount === 0) {
      return
    }

    const tagEls = container.querySelectorAll(
      '.TagBadge:not(.TagBadge--overflow)',
    )
    const containerTop = container.offsetTop
    let count = 0

    for (const el of tagEls) {
      const htmlEl = el as HTMLElement

      if (htmlEl.offsetTop + htmlEl.offsetHeight - containerTop > MAX_HEIGHT) {
        break
      }

      count += 1
    }

    if (count < totalCount) {
      count = Math.max(1, count - 1)
    }

    setVisibleCount(count)
  }, [totalCount])

  useLayoutEffect(() => {
    if (!isMeasuring) {
      return
    }

    const rafId = requestAnimationFrame(() => {
      measure()
    })

    return () => cancelAnimationFrame(rafId)
  }, [isMeasuring, measure])

  useLayoutEffect(() => {
    setVisibleCount(null)
  }, [totalCount])

  useLayoutEffect(() => {
    const container = containerRef.current

    if (!container) {
      return
    }

    let lastWidth = container.offsetWidth

    const ro = new ResizeObserver(() => {
      const newWidth = container.offsetWidth

      if (newWidth !== lastWidth) {
        lastWidth = newWidth
        setVisibleCount(null)
      }
    })

    ro.observe(container)

    return () => ro.disconnect()
  }, [])

  return {
    containerRef,
    isMeasuring,
    visibleCount: visibleCount ?? totalCount,
  }
}
